import dayjs from '@/lib/dayjs';

import { Constants } from '../constants/bankDossier';

/** Presentation helpers for the bank dossier blocks. */

export const EMPTY = Constants.emptyValue;

export const textOrEmpty = (value: string | null | undefined): string =>
  value && value.trim().length > 0 ? value : EMPTY;

export const numberOrEmpty = (value: number | null | undefined): string =>
  value == null ? EMPTY : value.toLocaleString('pt-BR');

export const moneyOrEmpty = (value: number | null | undefined): string => {
  if (value == null) return EMPTY;
  return `R$ ${value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const percentOrEmpty = (value: number | null | undefined): string =>
  value == null
    ? EMPTY
    : `${value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} %`;

export const booleanLabel = (value: boolean): string =>
  value ? Constants.yes : Constants.no;

// Date-only values carry no time to convert; datetimes without an offset are UTC.
const saoPaulo = (value: string): dayjs.Dayjs =>
  value.length === 10 ? dayjs(value) : dayjs.utc(value).tz('America/Sao_Paulo');

/** "05/03/2026". */
export const dossierDate = (value: string | null | undefined): string =>
  value ? saoPaulo(value).format('DD/MM/YYYY') : EMPTY;

/** "05/03/2026 13:12". */
export const dossierDateTime = (value: string | null | undefined): string =>
  value ? saoPaulo(value).format('DD/MM/YYYY HH:mm') : EMPTY;

/** "13:12". */
export const dossierTime = (value: string | null | undefined): string =>
  value ? saoPaulo(value).format('HH:mm') : '';

/** "356,99 KB" / "3,2 MB" from a byte count. */
export const fileSizeLabel = (bytes: number | null | undefined): string => {
  if (bytes == null) return EMPTY;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toLocaleString('pt-BR', {
      maximumFractionDigits: 2,
    })} KB`;
  }
  return `${(bytes / (1024 * 1024)).toLocaleString('pt-BR', {
    maximumFractionDigits: 2,
  })} MB`;
};

/** Formats a digits-only CPF/CNPJ; anything else is returned as received. */
export const documentOrEmpty = (value: string | null | undefined): string => {
  if (!value) return EMPTY;
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (digits.length === 14) {
    return digits.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      '$1.$2.$3/$4-$5'
    );
  }
  return value;
};

export const phoneOrEmpty = (value: string | null | undefined): string => {
  if (!value) return EMPTY;
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  if (digits.length === 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return value;
};

/** Tone hint for free-text sub-status pills. */
export const subStatusTone = (
  value: string | null | undefined
): 'neutral' | 'positive' | 'warning' | 'negative' | 'info' => {
  if (!value) return 'neutral';
  const text = value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  if (text.includes('cancel') || text.includes('rejeit')) return 'negative';
  if (text.includes('final') || text.includes('conclu')) return 'positive';
  if (
    text.includes('revis') ||
    text.includes('retific') ||
    text.includes('pendente') ||
    text.includes('aguard')
  ) {
    return 'warning';
  }
  if (text.includes('andamento') || text.includes('receb')) return 'info';
  return 'neutral';
};
